import { graphql } from 'app/generated/gql'
import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'urql'
import { useEventStream } from 'app/hooks/useEventStream'
import { logWarn } from 'lib/logging'

/** The typed document is validated against the generated schema during codegen. */
const LOCATION_OPT_OUTS_QUERY = graphql(`
  query LocationOptOuts($search: String, $pagination: PaginationInput) {
    optOuts(search: $search, pagination: $pagination) {
      hits {
        id
        key
        createdDate
      }
      total
    }
  }
`)

type LocationOptOutsData = {
  optOuts?: { hits?: Array<{ id: string; key: string; createdDate: string }>; total?: number }
}

/** Stable identity so consumers' useMemo deps do not churn on every render. */
const EMPTY_OPT_OUTS: ReadonlyMap<string, string> = new Map<string, string>()

/**
 * Phone key -> opt-out `createdDate` for the location (keys are digits-only
 * last 10, same as `phoneKey`). Empty map until the first fetch resolves or
 * when `locationId` is undefined. The refetch is load-bearing: only the backend
 * writes OptOut records, so nothing ever invalidates urql's cache for this
 * query and without it the map goes stale for the whole session. It refetches on
 * OptOut events from the live stream (a STOP creates one, a resubscribe deletes
 * one), so suppression moves in step with the opt-out itself. Past the 1000-record
 * bound it under-suppresses (visible, never hidden) and logs.
 */
export function useLocationOptOuts(locationId: string | undefined): ReadonlyMap<string, string> {
  const [result, reexecuteQuery] = useQuery<LocationOptOutsData>({
    query: LOCATION_OPT_OUTS_QUERY,
    variables: {
      search: locationId ? `type:phone AND locationId:"${locationId}"` : undefined,
      pagination: { limit: 1000 },
    },
    pause: !locationId,
    requestPolicy: `cache-and-network`,
  })

  // `network-only` on purpose: the whole point of the refetch is to bypass the
  // document cache, which nothing else invalidates because the SPA never
  // mutates OptOut records. An OptOut create/delete rides the stream as an
  // `event` frame with type OptOut for this location.
  useEventStream(
    `event`,
    (event) => {
      const payload = event.payload as { type?: string; data?: { locationId?: { to?: string } } }
      if (payload.type !== `OptOut`) return
      if (payload.data?.locationId?.to && payload.data.locationId.to !== locationId) return
      reexecuteQuery({ requestPolicy: `network-only` })
    },
    !!locationId,
  )

  const total = result.data?.optOuts?.total ?? 0
  const loaded = result.data?.optOuts?.hits?.length ?? 0
  const warnedLocationRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!locationId || total <= loaded) return
    // Once per location, not once per poll: the bound does not move between polls.
    if (warnedLocationRef.current === locationId) return
    warnedLocationRef.current = locationId
    logWarn(`Location opt-out list is truncated; some opted-out threads will not be suppressed`, {
      locationId,
      total,
      loaded,
    })
  }, [locationId, total, loaded])

  return useMemo(() => {
    const hits = result.data?.optOuts?.hits
    if (!hits?.length) return EMPTY_OPT_OUTS
    const optOuts = new Map<string, string>()
    for (const hit of hits) {
      if (!hit?.key || !hit.createdDate) continue
      const existing = optOuts.get(hit.key)
      // Duplicate keys keep the latest record: it is the one currently in force,
      // so a re-opt-out is respected rather than overridden by a stale earlier
      // record. Suppression compares a message against this timestamp, so the
      // later date is also the one that keeps a thread suppressed correctly.
      if (existing && existing >= hit.createdDate) continue
      optOuts.set(hit.key, hit.createdDate)
    }
    return optOuts.size > 0 ? optOuts : EMPTY_OPT_OUTS
  }, [result.data])
}
